// §4 S13 — Blocked stations, /production/blocked (Module 04).
//
// THE 07:00 QUESTION. A planner walking onto the floor asks one thing before
// anything else: which of my stations is not running, why, for how long, and
// what is stacking up behind it. Until this screen, the platform could answer
// none of it from the office. §5 e22 has served open downtime since 04-C-1 and
// had ZERO office consumers — the whole `open_only` filter existed for a floor
// probe and a walkthrough — and the dispatch board's cards dropped `qc_hold` on
// the floor even though the station queue has carried it since 04-C-5. The
// truth was in the database and on the wire, and no screen in the product said
// it.
//
// THIS IS A NEW SURFACE AND THE ARGUMENT IS ROLE-SHAPED, NOT AESTHETIC. The
// obvious cheaper move is a section on S4, whose columns already hold these
// queues. Two things rule it out, and both are §7 facts rather than taste:
//
//  1. **S4 is DEPARTMENT-scoped and demands two picks before it draws
//     anything.** "Which of my stations is down" is a FACILITY question asked
//     before the planner knows which department to look in — putting the answer
//     behind a department picker asks them to guess the answer to get it.
//  2. **S4's audience is not e22's audience.** The dispatch nav entry rides
//     `production.wo.write` (§4 S4: planner / facility_admin / group_admin),
//     and `maintenance` — the role §5 row 22 names FIRST among the people who
//     need a downtime list — holds neither that code nor any way to reach that
//     screen. A downtime surface living inside S4 would be invisible to the
//     role it exists for. This screen rides `production.telemetry.read`, which
//     IS e22's own fence, so its audience and its data agree by construction.
//
// The cheap half is still done where it belongs: S4's cards now carry the
// blocked markers they always had the data for. The two surfaces answer
// different questions — S4 is "what is the order of work", S13 is "what is
// stopped" — and neither is a copy of the other.
//
// WHAT THE BOARD PROMISES, and where each fact comes from:
//
//  * WHICH — one row per blocked station, named through module 16's work-centre
//    read (`name_i18n`), never its uuid (RefLabel's four honest states).
//  * WHY — the downtime reason's OWN label from the §2.6 catalog, resolved for
//    the reader's language (e23's `name_i18n`), plus the `kind`; and separately
//    "quality hold on the operation" for a held step (D-123 ruling 9's word —
//    never a bare "hold", because this product calls three unrelated things
//    that and a factory user meets all three).
//  * HOW LONG — elapsed from the row's own `started_at`. The server has no
//    duration to serve for an OPEN row (`duration_minutes` is generated and
//    NULL until `ended_at` lands), so it is computed here from a server
//    timestamp and re-rendered on a minute tick. It is never fabricated and
//    never frozen.
//  * WHAT IT COSTS — the operations queued behind the stop, off the station's
//    own e14 queue: how many are waiting and which one is next.
//  * WHERE TO ACT — the station's column on S4, the work order, and (for a
//    quality block) module 07's quarantine board.
//  * AND WHAT TO END (added 2026-09-25, D-299 owner ruling H, §5 e35). For a
//    holder of `production.conflict.resolve` every open stoppage carries an
//    END STOPPAGE control behind a confirm (note required, "running again
//    since" optional). This board is where the office ender lives because it
//    is the one office surface that lists open stoppages: an op-bound row
//    opened while an order was live blocks that order's §3.1 close after it
//    completes, and the floor can end it only from a station still showing
//    live work — which a finished order's station often is not. Gated on the
//    ENDER's code, not on this board's read code: planner and maintenance read
//    the board and change floor truth nowhere (§7), so for them the control is
//    ABSENT, not dead. On success the row leaves the list at once and the
//    board says which work order the stop was holding, so a refused close can
//    be retried from there.
//
// THE SCOPE IS STATED, BECAUSE THE GOOD STATE IS A CLAIM. "Nothing is blocked"
// is the answer a planner acts on by walking away, so the screen says how many
// stations it checked to reach it. A station the caller may not read (§7's e14
// read fence answers 404 exactly as a missing station does) is excluded from
// that count rather than counted as clear — an unreadable station is not a
// running one.
//
// Roles: group_admin / facility_admin / planner / maintenance
// (`production.telemetry.read` — 0024's code, the e20/e21/e22 role set).

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { Banner } from "../../../../components/toast";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useTerm } from "../../../onboarding/hooks/terminology";
import { EndStoppageDialog } from "../components/end-stoppage-dialog";
import { FacilityFilter } from "../components/facility-filter";
import { RefLabel } from "../components/ref-label";
import { useProductionPermissions } from "../hooks/permissions";
import {
  OPEN_DOWNTIME_READ_CAP,
  useDowntimeReasons,
  useFacilities,
  useOpenDowntime,
  useStationQueues,
  useWorkCenters,
  type DowntimeLog,
  type DowntimeLogEndResponse,
  type DowntimeReason,
  type StationQueueOperation,
  type StationQueueProbe,
} from "../hooks/production-queries";
import {
  elapsedSince,
  formatElapsed,
  formatTimestamp,
  localeLabel,
  type ElapsedParts,
} from "../lib/format";
import { validateBlockedSearch } from "./search-params";

/** How often the elapsed column re-computes. A downtime board whose durations
 * freeze while the page is open is worse than one that shows none: it looks
 * live and is not. Sixty seconds is the grain the column renders at, so a
 * faster tick would only burn frames on a number that cannot change. */
export const ELAPSED_TICK_MS = 60_000;

/** One open downtime interval, with the catalog row that names it. `reason` is
 * `undefined` when the catalog has no row for the id — a deactivated reason
 * still names the stop it once explained, so that is a resolution failure to
 * SAY, not a reason to hide the stop. */
export interface BlockedDowntime {
  readonly log: DowntimeLog;
  readonly reason: DowntimeReason | undefined;
}

/** A station with at least one reason it is not running. */
export interface BlockedStation {
  readonly workCenterId: string;
  readonly downtime: readonly BlockedDowntime[];
  /** Steps this station cannot work because quality failed them (§2.2). */
  readonly heldOperations: readonly StationQueueOperation[];
  /** Ready / in-progress / paused steps at this station — what is stacking up. */
  readonly queueDepth: number;
  /** The step the D-072 comparator says is next, or undefined on an empty or
   * unreadable queue. */
  readonly nextUp: StationQueueOperation | undefined;
  /** False when this station's queue could not be read: the row still renders
   * its downtime, and says the waiting count is unknown rather than "0". */
  readonly queueKnown: boolean;
  /** The oldest open interval's elapsed, or undefined when only a quality hold
   * blocks the station — the sort key, so the longest stop is read first. */
  readonly worstElapsed: ElapsedParts | undefined;
}

/**
 * The board's rows, PURE — every input is already fetched, nothing here calls
 * anything, so the ordering and the blocked/clear decision are unit-testable
 * without a network.
 *
 * ORDERING IS BY DAMAGE, and stated rather than left to the reader: longest
 * open downtime first (a stop that has run three hours has cost three hours),
 * then stations blocked only by a quality hold, then — within a tie — by
 * station id so two polls of an unchanged plant do not re-order the board under
 * a thumb.
 */
export function buildBlockedStations(input: {
  readonly downtime: readonly DowntimeLog[];
  readonly reasons: readonly DowntimeReason[];
  readonly probes: readonly StationQueueProbe[];
  readonly now: number;
}): BlockedStation[] {
  const reasonById = new Map(input.reasons.map((r) => [r.id, r]));
  const probeById = new Map(input.probes.map((p) => [p.workCenterId, p]));

  const downtimeByStation = new Map<string, BlockedDowntime[]>();
  for (const log of input.downtime) {
    const bucket = downtimeByStation.get(log.work_center_id) ?? [];
    bucket.push({ log, reason: reasonById.get(log.downtime_reason_id) });
    downtimeByStation.set(log.work_center_id, bucket);
  }

  const stationIds = new Set<string>([...downtimeByStation.keys()]);
  for (const probe of input.probes) {
    if (probe.operations.some((op) => op.qc_hold)) stationIds.add(probe.workCenterId);
  }

  const rows: BlockedStation[] = [];
  for (const workCenterId of stationIds) {
    const downtime = downtimeByStation.get(workCenterId) ?? [];
    const probe = probeById.get(workCenterId);
    // A station with open downtime but no readable queue is still a blocked
    // station; it just cannot say what is waiting. `undefined` (never probed,
    // e.g. an archived station that still has an open row) reads the same way.
    const queueKnown = probe !== undefined && !probe.isPending && !probe.isAbsent && !probe.isError;
    const operations = queueKnown ? probe.operations : [];
    const elapsed = downtime
      .map((d) => elapsedSince(d.log.started_at, input.now))
      .filter((e): e is ElapsedParts => e !== undefined)
      .sort((a, b) => b.totalMinutes - a.totalMinutes);
    rows.push({
      workCenterId,
      // Oldest first inside the row too: the stop that has been open longest is
      // the sentence the row leads with.
      downtime: [...downtime].sort((a, b) => a.log.started_at.localeCompare(b.log.started_at)),
      heldOperations: operations.filter((op) => op.qc_hold),
      queueDepth: operations.length,
      nextUp: operations[0],
      queueKnown,
      worstElapsed: elapsed[0],
    });
  }

  return rows.sort((a, b) => {
    const at = a.worstElapsed?.totalMinutes ?? -1;
    const bt = b.worstElapsed?.totalMinutes ?? -1;
    if (at !== bt) return bt - at;
    return a.workCenterId.localeCompare(b.workCenterId);
  });
}

export function BlockedStationsScreen() {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const navigate = useNavigate();
  const permissions = useProductionPermissions();

  // FAIL-CLOSED, AND THE READ IS NOT ISSUED AT ALL (§3.9's UX-4 rule): a role
  // without the code would otherwise watch a skeleton turn into a 403 error
  // card offering a Retry that can never succeed.
  const mayRead = permissions.has("production.telemetry.read");
  const refused = permissions.isResolved && !mayRead;
  // §5 e35's code, NOT the board's (D-299 ruling H): group_admin and
  // facility_admin end stoppages; planner and maintenance read this board and
  // change floor truth nowhere, so for them the control does not exist.
  const mayEnd = permissions.has("production.conflict.resolve");
  /** The open stoppage whose End confirm is open, if any. */
  const [ending, setEnding] = useState<{ log: DowntimeLog; reasonLabel: string } | undefined>(
    undefined,
  );
  /** The last end that landed — held HERE so it survives the refetch that
   * removes its row, and names the work order a refused close can retry. */
  const [lastEnded, setLastEnded] = useState<DowntimeLogEndResponse | undefined>(undefined);
  /** Every row ended from this screen, so each leaves the board at once. */
  const [endedIds, setEndedIds] = useState<ReadonlySet<string>>(() => new Set());

  const rawSearch = useSearch({ strict: false });
  const search = useMemo(
    () => validateBlockedSearch(rawSearch as Record<string, unknown>),
    [rawSearch],
  );
  // DEFAULT TO THE FIRST FACILITY, as S13's Pareto does (`pareto.tsx`'s
  // `effectiveFacility`). Without it a bare `/production/blocked` — which is
  // what the sidebar link and a pasted URL both are — left `facilityId` empty,
  // so `useWorkCenters` stayed DISABLED, `workCenters.isPending` stayed true
  // forever, and every station name in the primary column rendered as a
  // permanent `…` from `RefLabel`'s pending state. A planner opening the board
  // to see which stations are blocked was shown a table of ellipses, and
  // nothing on the screen said why. Found by adding this route to the
  // locale-viewport sweep, which measured the degraded screen rather than the
  // intended one.
  const facilities = useFacilities(mayRead);
  const facilityId = search.facility_id ?? facilities.data?.items[0]?.id ?? "";
  const downtime = useOpenDowntime(facilityId, mayRead);
  const reasons = useDowntimeReasons();
  // ACTIVE only, and this is the fan-out's bound. An archived station is one
  // nobody may be sent to, so probing its queue would spend a request to learn
  // that no work is waiting at a machine no work can be sent to. Its open
  // downtime (if any) still renders — off e22, which does not filter on status.
  const workCenters = useWorkCenters(facilityId.length > 0 ? facilityId : undefined, "active");

  const stationIds = useMemo(
    () => (workCenters.data?.items ?? []).map((wc) => wc.id),
    [workCenters.data],
  );
  // The quality-hold half needs a station set, and a facility is what bounds it
  // (see useStationQueues' header). With no facility picked the board answers
  // the downtime question across every plant the caller can see and SAYS that
  // the quality half is unchecked — a stated gap, never a silent one.
  const probes = useStationQueues(stationIds, mayRead && facilityId.length > 0);

  // The elapsed clock. Separate from the query's refetch on purpose: one keeps
  // the ROWS fresh, this keeps the DURATIONS honest, and a board whose "12 min"
  // never becomes "13 min" is lying at exactly the moment somebody is deciding
  // whether to walk over.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now());
    }, ELAPSED_TICK_MS);
    return () => {
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (downtime.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [downtime.error, navigate]);

  // An ended row is gone from the NEXT e22 page, and the hook also drops it
  // from the cached one; filtering here as well means the row leaves the board
  // the instant the end lands, whatever the cache's timing.
  const openLogs = useMemo(
    () => (downtime.data?.items ?? []).filter((log) => !endedIds.has(log.id)),
    [downtime.data, endedIds],
  );
  const rows = useMemo(
    () =>
      buildBlockedStations({
        downtime: openLogs,
        reasons: reasons.data ?? [],
        probes,
        now,
      }),
    [openLogs, reasons.data, probes, now],
  );

  const stationsChecked = probes.filter((p) => !p.isAbsent && !p.isError && !p.isPending).length;
  const stationsUnreadable = probes.filter((p) => p.isAbsent || p.isError).length;
  const overflowing = downtime.data?.next_cursor != null;

  const stationOf = (id: string) => workCenters.data?.items.find((wc) => wc.id === id);
  const stationName = (id: string) => {
    const wc = stationOf(id);
    const label =
      wc === undefined ? undefined : localeLabel(wc.name_i18n, i18n.language) || wc.code;
    return <RefLabel value={label} id={id} pending={workCenters.isPending} />;
  };

  const columns: DataTableColumn<BlockedStation>[] = [
    {
      key: "workCenterId",
      header: term("work_center"),
      id: true,
      render: (row) => (
        <span data-testid={`blocked-station-${row.workCenterId}`}>
          {stationName(row.workCenterId)}
        </span>
      ),
    },
    {
      key: "blockedBy",
      header: t("production.blocked.colBlockedBy"),
      label: t("production.blocked.colBlockedBy"),
      render: (row) => (
        <span className="blocked-chips">
          {row.downtime.map((entry) => (
            <StatusChip
              key={entry.log.id}
              variant="bad"
              className="status-chip"
              data-testid={`blocked-downtime-${entry.log.id}`}
            >
              {t("production.blocked.downtimeChip")}
            </StatusChip>
          ))}
          {row.heldOperations.map((op) => (
            <StatusChip
              key={op.id}
              variant="warn"
              className="status-chip"
              data-testid={`blocked-hold-${op.id}`}
            >
              {/* D-123 ruling 9: the word on the glass is the WHOLE phrase.
                  Three unrelated things in this platform are called a hold and
                  a planner with this board and the quarantine board open meets
                  two of them on one screen. */}
              {t("production.blocked.qualityHoldChip")}
            </StatusChip>
          ))}
        </span>
      ),
    },
    {
      key: "why",
      header: t("production.blocked.colWhy"),
      render: (row) => (
        <span className="blocked-why">
          {row.downtime.map((entry) => (
            <span key={entry.log.id} className="blocked-why-line">
              {/* The reason's OWN label, in the reader's language — the same
                  words S10's grid and S11 use. A raw `DIE_CHANGE` on one
                  surface and «Смена фильеры» on another is one fact with two
                  vocabularies. When the catalog cannot name it, the id reads as
                  an identifier, never as a name. */}
              <RefLabel
                value={
                  entry.reason === undefined
                    ? undefined
                    : localeLabel(entry.reason.name_i18n, i18n.language) || entry.reason.code
                }
                id={entry.log.downtime_reason_id}
                pending={reasons.isPending}
              />
              {entry.reason?.triggers_maintenance === true ? (
                <span className="production-badge is-warn" data-testid="blocked-maintenance">
                  {t("production.blocked.triggersMaintenance")}
                </span>
              ) : null}
              {entry.log.notes !== null && entry.log.notes.length > 0 ? (
                <span className="production-hint blocked-notes">{entry.log.notes}</span>
              ) : null}
              {/* §5 e35 (D-299 ruling H): ABSENT without the ender's code —
                  a dead button would claim an authority the reader lacks. */}
              {mayEnd ? (
                <Button
                  size="sm"
                  className="blocked-end"
                  data-testid={`blocked-end-${entry.log.id}`}
                  onClick={() => {
                    setEnding({
                      log: entry.log,
                      reasonLabel:
                        entry.reason === undefined
                          ? t("production.blocked.end.unknownReason")
                          : localeLabel(entry.reason.name_i18n, i18n.language) || entry.reason.code,
                    });
                  }}
                >
                  {t("production.blocked.end.action")}
                </Button>
              ) : null}
            </span>
          ))}
          {row.heldOperations.map((op) => (
            <span key={op.id} className="blocked-why-line" data-testid={`blocked-held-op-${op.id}`}>
              {t("production.blocked.heldStep", { wo: op.wo_no, seq: op.seq_no })}
            </span>
          ))}
          {/* The failing inspection is NOT on this wire. e14 carries the flag
              and not the verdict behind it, so the board names the STEP and
              points at the quarantine board rather than inventing an inspection
              reference it does not have. */}
          {row.heldOperations.length > 0 ? (
            <span className="production-hint">{t("production.blocked.heldWhereToLook")}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: "worstElapsed",
      header: t("production.blocked.colFor"),
      age: true,
      render: (row) => (
        <span data-testid={`blocked-elapsed-${row.workCenterId}`}>
          {row.worstElapsed === undefined ? (
            // A station blocked only by a quality hold has no interval to
            // measure: `qc_hold` is a boolean with no timestamp beside it on
            // this wire. An invented "since" would be the worst kind of number.
            <span className="production-hint">{t("production.common.notAvailable")}</span>
          ) : (
            <>
              {formatElapsed(t, row.worstElapsed)}
              <span className="production-hint blocked-since">
                {t("production.blocked.since", {
                  time: formatTimestamp(row.downtime[0]?.log.started_at ?? "", i18n.language),
                })}
              </span>
            </>
          )}
        </span>
      ),
    },
    {
      key: "queueDepth",
      header: t("production.blocked.colWaiting"),
      numeric: true,
      render: (row) => (
        <span data-testid={`blocked-waiting-${row.workCenterId}`}>
          {!row.queueKnown ? (
            // NOT "0". An unreadable queue and an empty one are different
            // facts, and the expensive direction is believing nothing is
            // stacking up behind a stopped machine.
            <span className="production-hint">{t("production.blocked.waitingUnknown")}</span>
          ) : (
            <>
              {t("production.blocked.waitingCount", { count: row.queueDepth })}
              {row.nextUp !== undefined ? (
                <span className="production-hint blocked-next">
                  {t("production.blocked.nextUp", {
                    wo: row.nextUp.wo_no,
                    product: row.nextUp.product_name,
                  })}
                </span>
              ) : null}
            </>
          )}
        </span>
      ),
    },
    {
      key: "actions",
      header: t("production.common.actions"),
      actions: true,
      render: (row) => {
        const wc = stationOf(row.workCenterId);
        const firstWo = row.heldOperations[0]?.work_order_id ?? row.nextUp?.work_order_id;
        return (
          <span className="blocked-actions">
            {wc !== undefined && wc.department_id !== null ? (
              <Link
                to="/production/dispatch"
                search={{ facility_id: wc.facility_id, department_id: wc.department_id }}
                className="ds-lnk"
                data-testid={`blocked-open-queue-${row.workCenterId}`}
              >
                {t("production.blocked.openQueue")}
              </Link>
            ) : null}
            {firstWo !== undefined ? (
              <Link
                to="/production/work-orders/$id"
                params={{ id: firstWo }}
                className="ds-lnk"
                data-testid={`blocked-open-wo-${row.workCenterId}`}
              >
                {t("production.blocked.openWorkOrder")}
              </Link>
            ) : null}
            {row.heldOperations.length > 0 ? (
              <Link
                to="/quality/quarantine"
                className="ds-lnk"
                data-testid={`blocked-open-quarantine-${row.workCenterId}`}
              >
                {t("production.blocked.openQuarantine")}
              </Link>
            ) : null}
          </span>
        );
      },
    },
  ];

  const paused = downtime.fetchStatus === "paused";
  const loading = !refused && downtime.isPending && !paused;
  const failed = downtime.error !== null && !(downtime.error instanceof UnauthorizedError);

  return (
    <section className="blocked-screen" data-testid="blocked-screen">
      <PageHeader title={t("production.blocked.title")} />

      {refused ? null : (
        <div className="blocked-filters" data-testid="blocked-filters">
          {/* ALL FACILITIES IS ALLOWED and is the default: "which of my
              stations is down" must be answerable with zero picks at 07:00.
              Picking one narrows e22 AND turns on the quality-hold half. */}
          <FacilityFilter
            value={facilityId}
            allowAll
            facilities={facilities.data?.items ?? []}
            testId="blocked-facility"
            onChange={(value) => {
              void navigate({
                to: "/production/blocked",
                search: value.length > 0 ? { facility_id: value } : {},
                replace: true,
              });
            }}
          />
        </div>
      )}

      {/* THE END THAT LANDED (D-299 ruling H): what ended and, when the stop
          was holding one, the work order whose close can now be retried. */}
      {!refused && lastEnded !== undefined ? (
        <Banner
          kind="ok"
          data-testid="blocked-end-done"
          onDismiss={() => {
            setLastEnded(undefined);
          }}
          dismissLabel={t("production.common.close")}
        >
          {t("production.blocked.end.done", {
            time: formatTimestamp(lastEnded.downtime_log.ended_at ?? "", i18n.language),
          })}
          {lastEnded.work_order_id !== null ? (
            <>
              {" "}
              <Link
                to="/production/work-orders/$id"
                params={{ id: lastEnded.work_order_id }}
                className="ds-lnk"
                data-testid="blocked-end-done-wo"
              >
                {t("production.blocked.end.openHeldOrder")}
              </Link>
            </>
          ) : null}
        </Banner>
      ) : null}

      {ending !== undefined ? (
        <EndStoppageDialog
          log={ending.log}
          reasonLabel={ending.reasonLabel}
          onClose={() => {
            setEnding(undefined);
          }}
          onEnded={(result) => {
            setLastEnded(result);
            setEndedIds((prior) => new Set([...prior, result.downtime_log.id]));
            setEnding(undefined);
          }}
        />
      ) : null}

      {refused ? (
        <ForbiddenState
          title={t("production.blocked.forbiddenTitle")}
          body={t("production.blocked.forbiddenBody")}
        />
      ) : null}

      {!refused && paused ? (
        <OfflineState
          title={t("production.blocked.offlineTitle")}
          body={t("production.blocked.offlineBody")}
        />
      ) : null}

      {loading ? (
        <TableSkeleton rows={4} cols={columns.length} label={t("production.common.loading")} />
      ) : null}

      {!refused && failed ? (
        <ErrorState
          title={t("production.blocked.error")}
          retryLabel={t("production.common.retry")}
          requestIdLabel={t("production.common.requestId")}
          requestId={requestIdOf(downtime.error)}
          onRetry={() => {
            void downtime.refetch();
          }}
        />
      ) : null}

      {!refused && !loading && !paused && !failed && rows.length === 0 ? (
        // THE GOOD STATE, SAID PLAINLY. This is the answer a planner acts on by
        // walking away, so it names its own scope on the line below.
        <EmptyState
          title={t("production.blocked.emptyTitle")}
          body={
            facilityId.length > 0
              ? t("production.blocked.emptyBody", { count: stationsChecked })
              : t("production.blocked.emptyBodyAllFacilities")
          }
        />
      ) : null}

      {!refused && rows.length > 0 ? (
        <DataTable
          data-testid="blocked-table"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.workCenterId}
          rowTestId="blocked-row"
          empty={null}
          caption={t("production.blocked.tableCaption")}
        />
      ) : null}

      {/* The scope line is a CLAIM about how much of the plant was looked at,
          so it may only appear once there is an answer to qualify. Rendered
          while the read is in flight it would say "0 blocked" over a skeleton,
          and rendered after a failure it would say "0 blocked" over an error
          card — both of which read as the good state at the exact moment the
          screen does not have one. */}
      {!refused && !paused && !loading && !failed ? (
        <p className="production-hint blocked-scope" data-testid="blocked-scope">
          {facilityId.length > 0
            ? t("production.blocked.scopeFacility", {
                checked: stationsChecked,
                blocked: rows.length,
              })
            : t("production.blocked.scopeAllFacilities", { blocked: rows.length })}
          {stationsUnreadable > 0 ? (
            <> {t("production.blocked.scopeUnreadable", { count: stationsUnreadable })}</>
          ) : null}
        </p>
      ) : null}

      {/* A read that stopped at its bound is TOLD, never truncated silently —
          the reader learns the list is incomplete rather than trusting a short
          one. Since D-299 round 3 the hook follows e22's cursor to the end, so
          this fires only past OPEN_DOWNTIME_READ_CAP open stoppages. */}
      {!refused && overflowing ? (
        <p className="production-note" role="status" data-testid="blocked-overflow">
          {/* The number in the sentence IS the bound the read stopped at — the
              constant, never a literal typed twice. */}
          {t("production.blocked.overflow", { count: OPEN_DOWNTIME_READ_CAP })}
        </p>
      ) : null}
    </section>
  );
}
